<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import Modal from "@/components/common/Modal.vue";

import type {
  ManualSharingChoice,
  ManualSharingPhase,
} from "@/composables/useManualSharingConsent";
const props = withDefaults(defineProps<{ phase?: ManualSharingPhase }>(), {
  phase: "choose",
});
const emit = defineEmits<{
  confirm: [choice: ManualSharingChoice];
  retry: [];
  close: [];
}>();
const pending = computed(() =>
  ["saving", "preparing", "copying"].includes(props.phase),
);
const failed = computed(() => props.phase.endsWith("_error"));
const selection = ref<"veeva" | "outlook" | null>(null);
const firstChoice = ref<HTMLInputElement>();
const cancelButton = ref<HTMLButtonElement>();
watch(
  pending,
  (value) => {
    if (value) cancelButton.value?.focus();
  },
  { flush: "post" },
);
let submitted = false;
onMounted(() => firstChoice.value?.focus());

const confirm = () => {
  if (!selection.value || submitted) return;
  submitted = true;
  emit("confirm", selection.value);
};

const trapFocus = (event: KeyboardEvent) => {
  if (event.key !== "Tab") return;
  const modal = event.currentTarget as HTMLElement;
  // Treat the radio group as one Tab stop. Handle every transition because
  // WebKit's default Tab navigation can skip native buttons entirely.
  const radio =
    modal.querySelector<HTMLInputElement>('input[type="radio"]:checked') ??
    firstChoice.value;
  const controls = Array.from(
    modal.querySelectorAll<HTMLElement>(
      'button:enabled, input[type="radio"]:enabled',
    ),
  ).filter((control) => control.tagName !== "INPUT" || control === radio);
  if (!controls.length) return;
  event.preventDefault();
  const index = controls.indexOf(document.activeElement as HTMLElement);
  const next =
    index < 0
      ? event.shiftKey
        ? controls.length - 1
        : 0
      : (index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
  controls[next].focus();
};
</script>

<template>
  <Modal
    :active="true"
    scrolling
    shrinkToContent
    class="manual_sharing_consent adaptive_footer"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('documentSpace.manualConsent.title')"
    data-test="manual-sharing-consent"
    @close="emit('close')"
    @keydown="trapFocus"
  >
    <template #header>{{ $t("documentSpace.manualConsent.title") }}</template>
    <template #body>
      <p class="introduction">
        {{ $t("documentSpace.manualConsent.introduction") }}
      </p>
      <fieldset :disabled="phase !== 'choose'">
        <legend>{{ $t("documentSpace.manualConsent.instruction") }}</legend>
        <label class="declaration">
          <input
            ref="firstChoice"
            v-model="selection"
            type="radio"
            name="sharing-consent"
            value="veeva"
          />
          <span>
            <strong>{{ $t("documentSpace.manualConsent.veevaTitle") }}</strong>
            <span>{{
              $t("documentSpace.manualConsent.veevaDeclaration")
            }}</span>
          </span>
        </label>
        <label class="declaration">
          <input
            v-model="selection"
            type="radio"
            name="sharing-consent"
            value="outlook"
          />
          <span>
            <strong>{{
              $t("documentSpace.manualConsent.outlookTitle")
            }}</strong>
            <span>{{
              $t("documentSpace.manualConsent.outlookDeclaration")
            }}</span>
          </span>
        </label>
      </fieldset>
      <p class="confirmation">
        {{ $t("documentSpace.manualConsent.confirmation") }}
      </p>
      <p
        v-if="pending || failed"
        role="status"
        aria-live="polite"
        data-test="manual-consent-status"
        class="status"
      >
        {{ $t(`documentSpace.manualConsent.${phase}`) }}
      </p>
    </template>
    <template #footer>
      <div class="buttons">
        <button
          type="button"
          class="button"
          ref="cancelButton"
          data-test="manual-consent-cancel"
          @click="emit('close')"
        >
          {{ $t("cancel") }}
        </button>
        <button
          type="button"
          class="button primary"
          data-test="manual-consent-continue"
          :disabled="pending || (!failed && (!selection || phase !== 'choose'))"
          @click="failed ? emit('retry') : confirm()"
        >
          {{
            $t(
              `documentSpace.manualConsent.${failed ? (phase === "copy_error" ? "copyAgain" : "retry") : "continue"}`,
            )
          }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<style scoped>
.manual_sharing_consent :deep(.window) {
  width: min(680px, calc(100vw - 40px));
}
.manual_sharing_consent :deep(.body) {
  overflow-y: auto;
}
p {
  margin: 0 0 1em;
  line-height: 1.5;
}
.introduction,
.confirmation {
  font-weight: 600;
}
.confirmation {
  margin: 1em 0 0;
}
.status {
  margin: 1em 0 0;
  font-weight: 600;
}
fieldset {
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
}
legend {
  margin-bottom: 0.8em;
  padding: 0;
}
.declaration {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  margin-bottom: 12px;
  border: 1px solid var(--classic-border, #dbdbdb);
  border-radius: 6px;
  background: var(--classic-surface-raised, #f9fafb);
  cursor: pointer;
  line-height: 1.5;
}
.declaration:last-child {
  margin-bottom: 0;
}
.declaration strong,
.declaration span span {
  display: block;
}
.declaration strong {
  margin-bottom: 4px;
}
.declaration input {
  flex: 0 0 auto;
  margin-top: 5px;
}
.declaration:focus-within {
  outline: 2px solid var(--classic-accent, #176f83);
  outline-offset: 2px;
}
button:focus-visible {
  outline: 2px solid var(--classic-accent, #176f83);
  outline-offset: 2px;
}
</style>
